import React from 'react';
import { styled } from '@mui/material';
import clsx from 'clsx';
import { Grid2 } from '../../Mui/Grid2';
import { FormItemNoContextLabel } from './FormItemLabel';
import { FormItemNoContextController } from './FormItemInput';
import { getStyle, formItemClasses } from './style';
import { useUniqueId } from '../../hooks/useUniqueId';
function FormItemNoContextItemBase(props, ref) {
    const { label, labelExtra, description, labelWidth, labelClassName, labelWrap, labelAlign, labelCol, wrapperCol, required, colon, tooltip, extra, className, requiredMark, help, children, status: validateStatus, disabled, id: _fieldId, errors, layout = 'vertical', countMessage, maxExceededIndicator = '', renderSortErrorsAndExtra, showCountPosition, } = props;
    const _id = useUniqueId('FormItem')('root');
    const fieldId = _fieldId ?? _id;
    const fieldErrors = help ? [help] : (errors ?? []);
    const rootCls = clsx(className, formItemClasses.root, {
        [`${formItemClasses.layout.vertical}`]: layout === 'vertical',
        [`${formItemClasses.layout.inline}`]: layout === 'inline',
        [`${formItemClasses.layout.horizontal}`]: layout === 'horizontal',
    });
    const itemCls = clsx(formItemClasses.item.root, {
        [`${formItemClasses.item.withHelp}`]: fieldErrors.length > 0 || maxExceededIndicator,
        [`${formItemClasses.item.hasError}`]: validateStatus === 'error',
    });
    const isRequired = !!required;
    let childNode = null;
    const getChildNode = () => {
        if (React.isValidElement(children)) {
            const { defaultValue, ...restChildrenProps } = children.props;
            if (defaultValue !== undefined) {
                console.warn('Warning: [Form.Item] `defaultValue` will not work on controlled Field. You should use `initialValues` of Form instead.');
            }
            const childProps = {
                ...restChildrenProps,
                disabled: restChildrenProps.disabled ?? disabled,
                status: validateStatus,
                onChange: (...args) => {
                    children.props.onChange?.(...args);
                },
            };
            if (!childProps.id) {
                childProps.id = fieldId;
            }
            if (description ?? countMessage) {
                childProps['aria-describedby'] = clsx(childProps['aria-describedby'], {
                    [`${fieldId}-desc`]: description,
                    [`${fieldId}_count_message`]: countMessage,
                });
            }
            if (extra) {
                childProps['aria-describedby'] = clsx(childProps['aria-describedby'], `${fieldId}_extra`);
            }
            if (fieldErrors.length > 0 || maxExceededIndicator) {
                childProps['aria-invalid'] = 'true';
                childProps['aria-errormessage'] = clsx(childProps['aria-errormessage'], {
                    [`${fieldId}_errormessage`]: fieldErrors.length > 0,
                    [`${fieldId}_max_exceeded_indicator_errormessage`]: maxExceededIndicator,
                });
            }
            if (isRequired) {
                childProps['aria-required'] = 'true';
            }
            return React.cloneElement(children, childProps);
        }
        // eslint-disable-next-line react/jsx-no-useless-fragment
        return children;
    };
    const renderLayout = (baseChildren) => (React.createElement("div", { className: itemCls },
        React.createElement(Grid2, { container: true, className: `${formItemClasses.item.root}-grid` },
            React.createElement(FormItemNoContextLabel, { label: label, labelWidth: labelWidth, className: labelClassName, required: isRequired, requiredMark: requiredMark, labelCol: labelCol, labelAlign: labelAlign, colon: colon, htmlFor: fieldId, tooltip: tooltip, labelWrap: labelWrap, labelExtra: labelExtra, layout: layout, description: description }),
            React.createElement(FormItemNoContextController, { errors: fieldErrors, extra: extra, fieldId: fieldId, wrapperCol: wrapperCol, maxExceededIndicator: maxExceededIndicator, countMessage: countMessage, renderSortErrorsAndExtra: renderSortErrorsAndExtra, showCountPosition: showCountPosition }, baseChildren))));
    if (Array.isArray(children)) {
        if (process.env.NODE_ENV !== 'production') {
            console.warn('Warning: A `FormItemBasic` must have a single child element.');
            // eslint-disable-next-line react/jsx-no-useless-fragment
            return React.createElement(React.Fragment, null, children);
        }
    }
    childNode = getChildNode();
    return (React.createElement("div", { className: clsx(className, formItemClasses.root, rootCls), ref: ref }, renderLayout(childNode)));
}
const StyledFormItemNoContextItem = styled(React.forwardRef(FormItemNoContextItemBase))((props) => {
    const { theme } = props;
    return getStyle(theme);
});
export const FormItemNoContextItem = React.forwardRef((props, ref) => (React.createElement(StyledFormItemNoContextItem, { ...props, ref: ref })));
